import { CommonModule, formatDate } from '@angular/common'
import { Component, Input, OnInit } from '@angular/core'
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms'
import { MatIconModule } from '@angular/material/icon'
import { ToastrService } from '@app/services/toastr.service'
import { NbButtonModule, NbDatepickerModule, NbDialogRef, NbFormFieldModule, NbInputModule } from '@nebular/theme'
import { PromoCode } from '@personas/interfaces/promo-code.interface'
import { PromoCodeService } from '@personas/services/promo-code.service'
import { Subject, takeUntil } from 'rxjs'

@Component({
  selector: 'ngx-create-promo-code',
  templateUrl: './create-promo-code.component.html',
  styleUrls: ['./create-promo-code.component.scss'],
  imports: [CommonModule, ReactiveFormsModule, FormsModule, NbFormFieldModule, MatIconModule, NbDatepickerModule, NbButtonModule, NbInputModule]
})
export class CreatePromoCodeComponent implements OnInit {
  /**
   * Current event being managed
   */
  @Input()
  event_id: number
  /**
   * List of promo codes available
   */
  @Input()
  promocodes: any[] = []
  /**
   * The current currency of the persona
   */
  @Input()
  currency: string
  /**
   * The promo code being edited
   */
  editing: PromoCode
  /**
   * The data form
   */
  form!: FormGroup
  private _destroy$ = new Subject()
  /**
   * Creates the component and inject it's dependencies
   * @param dialogRef NbDialogRef Service
   * @param promoCodeService PromoCodeService
   * @param fb FormBuilder
   */
  constructor(private dialogRef: NbDialogRef<CreatePromoCodeComponent>, private promoCodeService: PromoCodeService, private toastrService: ToastrService) {}

  /**
   * Initiallizes the component
   */
  ngOnInit() {
    this.form = new FormGroup({
      code: new FormControl(null, Validators.required),
      amount: new FormControl(null, [Validators.required, Validators.pattern('^[0-9]+(.[0-9]+)?$')]),
      max_usage: new FormControl(0, Validators.pattern('[0-9]*')),
      expiry_date: new FormControl(null),
      type: new FormControl('2', [Validators.required])
    })
  }

  ngOnDestroy(): void {
    this._destroy$.next(null)
    this._destroy$.complete()
  }

  /**
   * Closes the dialog
   * @param status
   */
  exit(status) {
    this.dialogRef.close(!!status)
  }

  /**
   * Creates a new promo code
   */
  create() {
    if (this.form.invalid) return this.form.markAllAsTouched()

    const form = this.form.value
    if (!form.expiry_date) {
      delete form.expiry_date
    } else {
      form.expiry_date = formatDate(form.expiry_date, 'yyyy-MM-dd', 'en-US')
    }
    if (!form.max_usage) {
      form.max_usage = 0
    }
    this.promoCodeService
      .createPromoCode(this.event_id, form)
      .pipe(takeUntil(this._destroy$))
      .subscribe((o: PromoCode) => {
        this.toastrService.success('Success!', 'Discount Code created.')
        this.promocodes.push(o[0])
        this.form.reset()
        this.form.get('type').patchValue('2')
      })
  }

  /**
   * Update promo code
   */
  update() {
    if (this.form.invalid) return this.form.markAllAsTouched()

    const form = this.form.value
    if (!form.expiry_date) {
      delete form.expiry_date
    }
    if (!form.max_usage) {
      form.max_usage = 0
    }
    if (!form.expiry_date) delete form.expiry_date
    else form.expiry_date = formatDate(form.expiry_date, 'yyyy-MM-dd', 'en-US')
    this.promoCodeService
      .updatePromoCode(this.editing.id, form)
      .pipe(takeUntil(this._destroy$))
      .subscribe((updatedObject: PromoCode) => {
        this.toastrService.success('Success!', 'Discount Code updated.')
        this.promocodes[this.promocodes.findIndex((o) => o.id === this.editing.id)] = updatedObject
        this.editing = null
        this.form.reset()
        this.form.get('type').patchValue('2')
      })
  }

  /**
   * Delete promo code
   */
  delete() {
    this.promoCodeService
      .deletePromoCode(this.editing.id)
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {
        this.toastrService.success('Success!', 'Discount Code deleted.')
        this.promocodes.splice(
          this.promocodes.findIndex((o) => o.id === this.editing.id),
          1
        )
        this.editing = null
        this.form.reset()
        this.form.get('type').patchValue('2')
      })
  }

  /**
   * Update the form with the selected promo code values
   * @param promocode
   */
  selectCode(promocode: PromoCode) {
    this.form.reset()
    if (this.editing === promocode) this.editing = null
    else {
      this.editing = promocode
      this.form.patchValue({
        id: promocode.id,
        code: promocode.code,
        amount: promocode.amount,
        max_usage: promocode.max_usage,
        expiry_date: promocode.expiry_date ? new Date(promocode.expiry_date) : '',
        type: promocode.type.toString()
      })
    }
  }
}
